// pages/ApplicationToLease.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { onApiCallRentalSend } from "../../Core/Apicall";
import { toast } from "react-toastify";
import { sitekey } from "../../Core/ApiEndPoint";
import ReCAPTCHA from "react-google-recaptcha";

const NAVY = "#1F5C8A";

function BlankApplicant() {
    return {
        current_address: "",
        how_long: "",
        reason_moving: "",
        employer: "",
        job_title: "",
        monthly_income: "",
    };
}

export default function ApplicationToLease() {
    const { t, i18n } = useTranslation();
    const [buildingAddress, setBuildingAddress] = useState("");
    const [fullName, setFullName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [moveInDate, setMoveInDate] = useState("");
    const [numOccupants, setNumOccupants] = useState("");
    const [petsYes, setPetsYes] = useState(false);
    const [petsInfo, setPetsInfo] = useState("");
    const [applicants, setApplicants] = useState([BlankApplicant()]);
    const [signature, setSignature] = useState("");
    const [signatureDate, setSignatureDate] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);


    const addApplicant = () => setApplicants((prev) => [...prev, BlankApplicant()]);
    const removeApplicant = (index) => setApplicants((prev) => prev.filter((_, i) => i !== index));
    const updateApplicant = (index, key, value) =>
        setApplicants((prev) => prev.map((a, i) => (i === index ? { ...a, [key]: value } : a)));

    function validate() {
        if (!buildingAddress.trim()) return t("ApplicationToLease.errors.buildingAddress");
        if (!fullName.trim()) return t("ApplicationToLease.errors.fullName");
        if (!phoneNumber.trim()) return t("ApplicationToLease.errors.phoneNumber");
        if (!emailAddress.trim()) return t("ApplicationToLease.errors.emailAddress");
        if (!signature.trim()) return t("ApplicationToLease.errors.signature");
        if (!signatureDate) return t("ApplicationToLease.errors.signatureDate");

        for (let i = 0; i < applicants.length; i++) {
            const a = applicants[i];
            if (!a.current_address.trim()) return t("ApplicationToLease.errors.applicantCurrentAddress", { i: i + 1 });
            if (!a.how_long.trim()) return t("ApplicationToLease.errors.applicantHowLong", { i: i + 1 });
        }
        return null;
    }
    const lang = i18n.language;

    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        setSubmitting(true);

        const data = {
            buildingAddress,
            fullName,
            phoneNumber,
            emailAddress,
            moveInDate,
            numOccupants,
            petsYes,
            petsInfo,
            applicants,
            signature,
            signatureDate,
            captchaToken
        };
        const params = `/?lang=${lang}`
        try {
            const res = await onApiCallRentalSend(data, params)
            setBuildingAddress("");
            setFullName("");
            setPhoneNumber("");
            setEmailAddress("");
            setMoveInDate("");
            setNumOccupants("");
            setPetsYes(false);
            setPetsInfo("");
            setApplicants([BlankApplicant()]);
            setSignature("");
            setSignatureDate("");
            setCaptchaToken(null);
            toast.success(res?.message || "Send successful");

        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Something went wrong!");
        } finally {
            setSubmitting(false);
        }
    };

    const navigate = useNavigate();

    return (
        <div style={{ fontFamily: "Inter, system-ui, Arial", padding: 20, background: "#f7f8fa", minHeight: "100vh" }}>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
                <div style={{ background: "#fff", padding: 22, borderRadius: 10, boxShadow: "0 6px 20px rgba(23,35,54,0.06)" }}>
                    <h1 style={{ marginTop: 0 }}>{t("ApplicationToLease.title")}</h1>

                    <form onSubmit={handleSubmit}>
                        <section style={{ marginBottom: 16 }}>
                            <label style={{ fontWeight: 700 }}>{t("ApplicationToLease.labels.unitApplying")}</label>
                            <input
                                value={buildingAddress}
                                onChange={(e) => setBuildingAddress(e.target.value)}
                                placeholder={t("ApplicationToLease.placeholders.buildingAddress")}
                                style={inputStyle}
                                required
                            />
                        </section>

                        <hr style={{ border: "none", borderTop: "1px solid #eef0f3", margin: "16px 0" }} />

                        <section style={{ marginBottom: 12 }}>
                            <label style={{ fontWeight: 700 }}>{t("ApplicationToLease.labels.basicInformation")}</label>
                            <div className="Application-ToLease" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t("ApplicationToLease.placeholders.fullName")} style={inputStyle} required />
                                <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder={t("ApplicationToLease.placeholders.phoneNumber")} style={inputStyle} required />
                                <input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} placeholder={t("ApplicationToLease.placeholders.emailAddress")} style={inputStyle} type="email" required />
                                <input value={moveInDate} onChange={(e) => setMoveInDate(e.target.value)} placeholder={t("ApplicationToLease.placeholders.moveInDate")} style={inputStyle} type="date" />
                                <input value={numOccupants} onChange={(e) => setNumOccupants(e.target.value)} placeholder={t("ApplicationToLease.placeholders.numOccupants")} style={inputStyle} />
                            </div>

                            <div style={{ marginTop: 10 }}>
                                <label style={{ fontWeight: 700 }}>{t("ApplicationToLease.labels.pets")}</label>
                                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                                    <label style={checkboxLabel}><input type="checkbox" checked={petsYes} onChange={(e) => setPetsYes(e.target.checked)} /> {t("ApplicationToLease.yes")}</label>
                                    <label style={checkboxLabel}><input type="checkbox" checked={!petsYes} onChange={() => setPetsYes(false)} /> {t("ApplicationToLease.no")}</label>
                                </div>
                                <input value={petsInfo} onChange={(e) => setPetsInfo(e.target.value)} placeholder={t("ApplicationToLease.placeholders.petsInfo")} style={inputStyle} />
                            </div>

                            <p style={{ color: "#555", fontSize: 14, marginTop: 10 }}>{t("ApplicationToLease.noteApplicants")}</p>
                        </section>

                        <hr style={{ border: "none", borderTop: "1px solid #eef0f3", margin: "16px 0" }} />

                        <section>
                            {applicants.map((appl, idx) => (
                                <div key={idx} style={{ border: "1px dashed #e2e6ea", padding: 12, borderRadius: 8, marginBottom: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <strong>{t("ApplicationToLease.applicantLabel", { n: idx + 1 })}</strong>
                                        <div>
                                            {applicants.length > 1 && (
                                                <button type="button" onClick={() => removeApplicant(idx)} style={smallBtn}>{t("ApplicationToLease.actions.remove")}</button>
                                            )}
                                        </div>
                                    </div>

                                    <label style={{ display: "block", marginTop: 10, fontWeight: 700 }}>{t("ApplicationToLease.labels.currentLiving")}</label>
                                    <input value={appl.current_address} onChange={(e) => updateApplicant(idx, "current_address", e.target.value)} placeholder={t("ApplicationToLease.placeholders.currentAddress")} style={inputStyle} required />
                                    <div className="Application-ToLease" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                        <input value={appl.how_long} onChange={(e) => updateApplicant(idx, "how_long", e.target.value)} placeholder={t("ApplicationToLease.placeholders.howLong")} style={inputStyle} required />
                                        <input value={appl.reason_moving} onChange={(e) => updateApplicant(idx, "reason_moving", e.target.value)} placeholder={t("ApplicationToLease.placeholders.reasonMoving")} style={inputStyle} required />
                                    </div>

                                    <label style={{ display: "block", marginTop: 12, fontWeight: 700 }}>{t("ApplicationToLease.labels.employment")}</label>
                                    <input value={appl.employer} onChange={(e) => updateApplicant(idx, "employer", e.target.value)} placeholder={t("ApplicationToLease.placeholders.employer")} style={inputStyle} />
                                    <div className="Application-ToLease" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                        <input value={appl.job_title} onChange={(e) => updateApplicant(idx, "job_title", e.target.value)} placeholder={t("ApplicationToLease.placeholders.jobTitle")} style={inputStyle} />
                                        <input value={appl.monthly_income} onChange={(e) => updateApplicant(idx, "monthly_income", e.target.value)} placeholder={t("ApplicationToLease.placeholders.monthlyIncome")} style={inputStyle} />
                                    </div>
                                </div>
                            ))}

                            <div style={{ marginTop: 8 }}>
                                <button type="button" onClick={addApplicant} style={{ ...smallBtn, border: `2px solid ${NAVY}`, color: NAVY }}>
                                    + {t("ApplicationToLease.actions.addApplicant")}
                                </button>
                            </div>
                        </section>

                        <hr style={{ border: "none", borderTop: "1px solid #eef0f3", margin: "16px 0" }} />

                        <section style={{ marginBottom: "20px" }} >
                            <label style={{ fontWeight: 700 }}>{t("ApplicationToLease.labels.permission")}</label>
                            <p style={{ color: "#555" }}>
                                {t("ApplicationToLease.permissionText")}
                            </p>

                            <div className="Application-ToLease" style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 12, alignItems: "end" }}>
                                <div>
                                    <label style={{ display: "block", fontWeight: 700 }}>{t("ApplicationToLease.labels.signature")}</label>
                                    <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder={t("ApplicationToLease.placeholders.signature")} style={inputStyle} required />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontWeight: 700 }}>{t("ApplicationToLease.labels.date")}</label>
                                    <input value={signatureDate} onChange={(e) => setSignatureDate(e.target.value)} type="date" style={inputStyle} required />
                                </div>
                            </div>
                        </section>
                        <ReCAPTCHA
                            sitekey={sitekey}
                            required
                            onChange={(token) => setCaptchaToken(token)}
                        />

                        <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                            <button type="submit" disabled={submitting} style={primaryBtn}>
                                {submitting ? t("ApplicationToLease.submitting") : t("ApplicationToLease.actions.submit")}
                            </button>

                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                style={{ ...outlineBtn, border: `2px solid ${NAVY}`, color: NAVY }}
                            >
                                {t("ApplicationToLease.actions.goBack")}
                            </button>
                        </div>

                        {message && (
                            <div style={{ marginTop: 12 }}>
                                <div style={{ color: message.type === "error" ? "#a00" : "green" }}>{message.text}</div>
                                {message.action && (
                                    <div style={{ marginTop: 8 }}>
                                        <button onClick={message.action} style={outlineBtn}>{t("ApplicationToLease.actions.openEmailClient")}</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}

/* Simple shared styles */
const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #d7dbe0",
    boxSizing: "border-box",
    marginTop: 8,
};

const primaryBtn = {
    background: NAVY,
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
};

const outlineBtn = {
    background: "#fff",
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
};

const smallBtn = {
    background: "#fff",
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #ddd",
    cursor: "pointer",
    fontSize: 13,
};

const checkboxLabel = { display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" };
